import React, { useEffect, useMemo, useState } from 'react';
import SideBarModal from '@/components/Modal/SideBarModal';
import { Button } from '@/components/Button';
import { createSandboxShipment, SandboxExchange } from '@/services/api/sandbox';
import { extractApiErrorMessage } from '@/utils/errorHandler';

type SimType = 'DOCKING' | 'DIRECT_2W';

const rand = (n: number) =>
  Array.from({ length: n }, () =>
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.charAt(
      Math.floor(Math.random() * 36)
    )
  ).join('');

const newBookingId = () => `SBX-${rand(8)}`;

/** Every sandbox shipment starts at a real Dash hub (Kemang, South Jakarta). */
export const SANDBOX_ORIGIN = {
  address:
    'Dash Hub Jakarta Selatan - Kemang, Jl. Kemang Selatan ID 2-8, RT.6/RW.2, Bangka, Kec. Mampang Prpt., Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12730',
  latitude: -6.265523,
  longitude: 106.818314,
};

/** A same-city drop from the Kemang hub (Tebet, South Jakarta). */
export const SANDBOX_DESTINATION = {
  address:
    'Jl. Tebet Raya No. 12, Tebet Tim., Kec. Tebet, Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12820',
  latitude: -6.229671,
  longitude: 106.854134,
};

/**
 * Creates one sandbox shipment with a fixed, realistic body — the same call a
 * partner makes with their dash_test_ key. Reusing a booking id answers
 * EXISTS (idempotency) and opens that shipment instead.
 */
const NewSandboxShipmentModal: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Called with the API exchange and the shipment it created (or found). */
  onCreated: (shipmentId: string, exchange: SandboxExchange) => void;
}> = ({ open, onClose, onCreated }) => {
  const [bookingId, setBookingId] = useState(newBookingId);
  const [type, setType] = useState<SimType>('DOCKING');
  const [withOrigin, setWithOrigin] = useState(true);
  const [itemCount, setItemCount] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{
    code: string | null;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setBookingId(newBookingId());
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  // No first-mile pickup in the simulator yet (no status for it): DOCKING is
  // always a drop-off (no needsPickup) at Dash Hub Kemang; DIRECT_2W is
  // point-to-point from the same hub, ticked by default (untick it to see
  // ORIGIN_REQUIRED).
  const body = useMemo(
    () => ({
      bookingId,
      type,
      sender: { name: 'PT Contoh Sejahtera', phone: '+62811111111' },
      recipient: { name: 'Apotek Fatih Farma', phone: '+62822222222' },
      ...(type === 'DOCKING' || withOrigin ? { origin: SANDBOX_ORIGIN } : {}),
      destination: SANDBOX_DESTINATION,
      items: Array.from({ length: itemCount }, (_, i) => ({
        name: `${bookingId}-ITEM-${i + 1}`,
        quantity: 1,
        weight: 1.5,
      })),
    }),
    [bookingId, type, withOrigin, itemCount]
  );

  const submit = async () => {
    if (submitting || !bookingId.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const exchange = await createSandboxShipment(body);
      const data = (exchange.response as { data?: { shipmentId?: string } })
        ?.data;
      if (exchange.status < 400 && data?.shipmentId) {
        onCreated(data.shipmentId, exchange);
        return;
      }
      const err = exchange.response as { error?: string; code?: string };
      setError({
        code: err?.code ?? null,
        message: err?.error ?? `HTTP ${exchange.status}`,
      });
    } catch (e) {
      setError({
        code: null,
        message: extractApiErrorMessage(e, 'Could not reach the API'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SideBarModal
      isOpen={open}
      onClose={onClose}
      width="md"
      className="max-w-[calc(100vw-2rem)]"
      header={
        <div>
          <h2 className="text-lg font-semibold text-text-strong-950">
            New sandbox shipment
          </h2>
          <p className="mt-0.5 text-xs text-text-sub-600">
            POST /v1/integration/shipments, in sandbox mode.
          </p>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || !bookingId.trim()}>
            {submitting ? 'Creating…' : 'Create shipment'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 p-6">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label
              htmlFor="sbx-booking"
              className="text-sm font-medium text-text-strong-950"
            >
              Booking ID
            </label>
            <button
              type="button"
              onClick={() => setBookingId(newBookingId())}
              className="text-xs font-medium text-primary hover:underline"
            >
              New ID
            </button>
          </div>
          <input
            id="sbx-booking"
            type="text"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
            className="w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 font-mono text-sm text-text-strong-950 focus:border-primary focus:outline-none"
          />
          <p className="mt-1 text-xs text-text-sub-600">
            The idempotency key — reuse one to get EXISTS back.
          </p>
        </div>

        <fieldset>
          <legend className="mb-1.5 text-sm font-medium text-text-strong-950">
            Type
          </legend>
          <div className="inline-flex rounded-lg border border-stroke-soft-200 p-0.5">
            {(['DOCKING', 'DIRECT_2W'] as SimType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setType(t);
                  setWithOrigin(true);
                }}
                aria-pressed={type === t}
                className={`rounded-md px-3 py-1.5 font-mono text-xs font-medium transition-colors ${
                  type === t
                    ? 'bg-(--primary-alpha-10) text-primary'
                    : 'text-text-sub-600 hover:text-text-strong-950'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-text-sub-600">
            {type === 'DOCKING'
              ? 'Dropped off at Dash Hub Kemang (sent as the origin).'
              : 'Picked up at the origin, Dash Hub Kemang.'}
          </p>
        </fieldset>

        {type === 'DIRECT_2W' && (
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={withOrigin}
              onChange={(e) => setWithOrigin(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span className="text-sm text-text-strong-950">
              Include an origin
              <span className="block text-xs text-text-sub-600">
                Required for DIRECT_2W — untick it to see ORIGIN_REQUIRED.
              </span>
            </span>
          </label>
        )}

        <div>
          <label
            htmlFor="sbx-items"
            className="mb-1.5 block text-sm font-medium text-text-strong-950"
          >
            Items
          </label>
          <input
            id="sbx-items"
            type="number"
            min={1}
            max={9}
            value={itemCount}
            onChange={(e) =>
              setItemCount(
                Math.min(9, Math.max(1, Number(e.target.value) || 1))
              )
            }
            className="w-20 rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 font-mono text-sm text-text-strong-950 focus:border-primary focus:outline-none"
          />
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg bg-error-lighter px-3 py-2 text-sm text-error-dark"
          >
            {error.code && (
              <code className="mr-1.5 font-mono text-xs font-semibold">
                {error.code}
              </code>
            )}
            {error.message}
          </div>
        )}
      </div>
    </SideBarModal>
  );
};

export default NewSandboxShipmentModal;
